import { LitElement, html, css, PropertyValues } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { TemplateResult } from 'lit';
import { classMap } from 'lit/directives/class-map.js';
import { unsafeSVG } from 'lit/directives/unsafe-svg.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import * as Icons from './icons/svg';

interface IconPaths {
  [key: string]: string;
}

const iconPaths: IconPaths = Icons as IconPaths;

const SMALL_ICON_SIZE = 16;
const LARGE_ICON_SIZE = 24;

/**
 * Nile icon component.
 *
 * @tag nile-icon
 *
 */
@customElement('nile-icon')
export class NileIcon extends LitElement {
  /**
   * The name of the icon set
   * @attr icon set
   * @type {IconName | undefined}
   */
  @property({ type: String, reflect: true })
  public set?: string = 'local';

  /**
   * The name of the icon
   * @attr name
   * @type {IconName | undefined}
   */
  @property({ type: String, reflect: true })
  public name?: string;

  /**
   * A description of what the icon represents
   * @attr description
   */
  @property({ type: String, reflect: true })
  public description = '';

  /**
   * A path to a custom SVG file to display as the icon
   * @attr customSvgPath
   */
  @property({ type: String })
  public customSvgPath?: string;

  /**
   * A size of what the icon represents
   * @attr size
   */
  @property({ type: String, reflect: true })
  public size = SMALL_ICON_SIZE;

  @state()
  private _svg = '';

  @property({ type: String }) override title = 'agents';

  /**
   * Color
   */
  @property({ reflect: true }) color: any = '#7F7F7F';

  @property({ type: Boolean })
  public push = false;

  /**
   * Retain Viewbox
   */

  @property({ reflect: true }) frame: any = 16;

  static override styles = css`
    :host {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      contain: content;

      --nile-svg-stroke: #7F7F7F;
      --nile-svg-stroke-width: 1px;
      --nile-svg-height: 16px;
      --nile-svg-width: 16px;
    }

    .nds-icon {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .nds-icon--push {
      margin-right: 12px;
    }

    svg {
      stroke: var(--nile-svg-stroke);
      stroke-width: var(--nile-svg-stroke-width);
      height: var(--nile-svg-height);
      width: var(--nile-svg-width);
    }
  `;

  private _getIconSize(): number {
    return this.name?.endsWith('-small') ? SMALL_ICON_SIZE : LARGE_ICON_SIZE;
  }

  protected override async updated(
    changedProperties: PropertyValues
  ): Promise<void> {
    if (changedProperties.has('size')) {
      this.style.setProperty('--nile-svg-height', `${this.size}px`);
      this.style.setProperty('--nile-svg-width', `${this.size}px`);
    }
    if (changedProperties.has('color')) {
      this.style.setProperty('--nile-svg-stroke', `${this.color}`);
    }
    if (changedProperties.has('name') || changedProperties.has('customSvgPath')) {
      let iconName = this.name ? this.removeHyphens(this.name) : undefined;
      if (iconName && iconPaths[iconName]) {
        this._svg = atob(iconPaths[iconName]);
      } else if (this.customSvgPath) {
        this._svg = await this.fetchSvg(this.customSvgPath);
      } else {
        throw new Error(`No icon named "${this.name}"`);
      }
    }
  }

  private async fetchSvg(path: string): Promise<string> {
    const response = await fetch(path);

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return await response.text();
  }

  private removeHyphens(iconName: string) {
    return iconName.replace(/-/g, '');
  }


  get buttonClassMap() {
    return {
      'nds-icon--push': this.push,
    };
  }

  protected override render(): TemplateResult {
    const size = this.size;
    const color = this.color;

    /**
     * When icons are outside frame
     */
    const viewboxLogic = this.frame
      ? `0 0 ${this.frame} ${this.frame}`
      : '0 0 16 16';

      return html`
      <span class="nds-icon ${classMap(this.buttonClassMap)}">
        ${this.customSvgPath
          ? html`${unsafeHTML(this._svg)}`
          : html`<svg
              xmlns="http://www.w3.org/2000/svg"
              version="1.1"
              viewBox="${viewboxLogic}"
              class="icon ${classMap(this.buttonClassMap)}"
              role="img"
              aria-hidden=${this.description === ''}
              aria-label=${this.description || ''}
              height="${size}"
              width="${size}"
              focusable="true"
              fill="${color}"
            >
              ${unsafeSVG(this._svg)}
            </svg>`
        }
      </span>
    `;
  }
}

declare global {
 interface HTMLElementTagNameMap {
    'nile-icon': NileIcon;
  }
}
